import { create } from 'zustand';
import api from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { ROUTES } from '@/constants/routes.ts'
import { useAuth } from '@/hooks/useAuth';
import { isAdminOrAbove } from '@/lib/permissions';

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  password?: string;
  email: string;
  title?: string;
  language?: string;
  default_rate?: number;
  role?: number;
  yearly_target?: number;
  form_of_salary?: string;
  amount_of_salary?: number;
  salary_history?: string;
  years_of_experience?: number;
  firms : number[];
  groups?: number[];
  is_active?: boolean;
  preferences?: Record<string, any>;
}


export interface ContactPerson {
  id?: number;
  name: string;
  email: string;
}

export interface Client {
  id: number;
  name: string;
  email: string;
  address: string;
  country: string;
  vat_number: string;
  vat_setting: 'exempt' | 'reverse' | 'normal' | 'out_of_scope' | 'override';
  vat_rate: number;
  vat_override_message: string;
  client_contacts: number[];
  contacts: ContactPerson[];
  friendly_name?: string;
  firm: number | null;
  // Peppol fields
  peppol_enabled?: boolean;
  peppol_id?: string;
  peppol_scheme?: string;
}

export interface MatterPhase {
    id?: number;
    name: string;
    description: string;
    budget: number;
    order: number;
    start_date?: string | null;
    end_date?: string | null;
  }

export interface Matter {
    id: number;
    name: string;
    group_name: string;
    billing_rates: {
      user: number;
      amount: number;
    }[];
    fee_sharing_configs?: import('@/types/fee-sharing').FeeSharingConfig[];
    phases?: MatterPhase[];
    budget: number;
    billable: boolean;
    email_on_budget_hit: boolean;
    rounding: boolean;
    rounding_type: 'up' | 'down' | 'nearest';
    rounding_amount: number;
    status: 'active' | 'inactive';
  }

interface Store {
  current_user: User;
  clients: Client[];
  users: User[];
  matters: Matter[];
  isLoading: boolean;
  fetchClients: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  fetchMatters: () => Promise<void>;
  fetchAll: () => Promise<void>;
}

export const useStore = create<Store>((set) => ({
  current_user: {} as User,
  clients: [],
  users: [],
  matters: [],
  isLoading: false,

  fetchClients: async () => {
    try {
      const res = await api.get(`${ROUTES.API.CLIENTS}?limit=9999`);
      set({ clients: res.data.results });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch clients',
        variant: 'destructive',
      });
    }
  },

  fetchUsers: async () => {
    try {
      // Get the current user from auth store
      const currentUser = useAuth.getState().user;
      const userGroups = currentUser?.groups || [];
      
      // Only fetch users if current user is a super user or admin
      if (isAdminOrAbove(userGroups)) {
        const res = await api.get(`${ROUTES.API.USERS}?limit=9999`);
        set({ users: res.data.results });
      } else {
        // For non-admins, just set an empty array or only include the current user
        set({ users: currentUser ? [currentUser] : [] });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive',
      });
    }
  },

  fetchMatters: async () => {
    try {
      const res = await api.get(`${ROUTES.API.MATTERS}?limit=9999`);
      set({ matters: res.data.results });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch matters',
        variant: 'destructive',
      });
    }
  },

  fetchAll: async () => {
    set({ isLoading: true });
    await Promise.all([
      useStore.getState().fetchClients(),
      useStore.getState().fetchMatters(),
      useStore.getState().fetchUsers(),
    ]);
    set({ isLoading: false });
  },
}));